import { matchPath } from 'react-router-dom';
import { ERoute } from 'common/const';
import { IToken } from 'entities/Auth/Auth.models';
import { ESettingsMenuTab, ESettingsTab } from 'entities/Settings/Settings.enum';
import { EUserRole } from 'entities/User/User.enum';
import { ERfpStatus } from 'entities/Rfp/Rfp.enum';

type AllowedRoutes = Record<
  string,
  {
    roles: EUserRole[];
    isSeller?: boolean;
  }[]
>;

export const allowedSettingsRoutes: AllowedRoutes = {
  // --- /settings/:tab/:menu ---
  [`${ERoute.Settings}/${ESettingsTab.Account}/${ESettingsMenuTab.Contracts}`]: [
    { roles: [EUserRole.AccountAdmin], isSeller: false },
  ],
  [`${ERoute.Settings}/${ESettingsTab.Account}/${ESettingsMenuTab.PersonalLimits}`]: [
    { roles: [EUserRole.AccountAdmin], isSeller: false },
  ],
  [`${ERoute.Settings}/${ESettingsTab.Account}/${ESettingsMenuTab.LegalEntityList}`]: [
    { roles: [EUserRole.AccountAdmin], isSeller: false },
  ],
  [`${ERoute.Settings}/${ESettingsTab.Account}/${ESettingsMenuTab.StockList}`]: [
    { roles: [EUserRole.AccountAdmin], isSeller: true },
  ],
  [`${ERoute.Settings}/${ESettingsTab.Account}/${ESettingsMenuTab.Integrations}`]: [
    { roles: [EUserRole.AccountAdmin], isSeller: true },
  ],
  [`${ERoute.Settings}/${ESettingsTab.Account}/${ESettingsMenuTab.UserList}`]: [{ roles: [EUserRole.AccountAdmin] }],
  [`${ERoute.Settings}/${ESettingsTab.Account}/${ESettingsMenuTab.AddressList}`]: [
    { roles: [EUserRole.AccountAdmin], isSeller: false },
  ],

  // --- /settings/:tab/:id/:itemMenu ---
  [`${ERoute.Settings}/${ESettingsTab.Subdivision}/:id/${ESettingsMenuTab.General}`]: [
    { roles: [EUserRole.AccountAdmin], isSeller: false },
  ],
  [`${ERoute.Settings}/${ESettingsTab.Subdivision}/:id/${ESettingsMenuTab.UserList}`]: [
    { roles: [EUserRole.AccountAdmin], isSeller: false },
  ],
  [`${ERoute.Settings}/${ESettingsTab.Subdivision}/:id/${ESettingsMenuTab.AddressList}`]: [
    { roles: [EUserRole.AccountAdmin], isSeller: false },
  ],
  [`${ERoute.Settings}/${ESettingsTab.Contract}/:id/${ESettingsMenuTab.General}`]: [
    { roles: [EUserRole.AccountAdmin], isSeller: true },
    { roles: [EUserRole.SellerManager] },
  ],
  [`${ERoute.Settings}/${ESettingsTab.Contract}/:id/${ESettingsMenuTab.Assortment}`]: [
    { roles: [EUserRole.AccountAdmin], isSeller: true },
    { roles: [EUserRole.SellerManager] },
  ],
  [`${ERoute.Settings}/${ESettingsTab.Contract}/:id/${ESettingsMenuTab.DiscountList}`]: [
    { roles: [EUserRole.AccountAdmin], isSeller: true },
    { roles: [EUserRole.SellerManager] },
  ],
  [`${ERoute.Settings}/${ESettingsTab.Contract}/:id/${ESettingsMenuTab.DeliveryTimeList}`]: [
    { roles: [EUserRole.AccountAdmin], isSeller: true },
    { roles: [EUserRole.SellerManager] },
  ],
  // --- /settings/:tab ---
  [`${ERoute.Settings}/${ESettingsTab.Contract}`]: [{ roles: [EUserRole.SellerManager] }],
};

export const allowedRfpRoutes: AllowedRoutes = {
  // --- /rfp-list/:role/:tab ---
  [`${ERoute.RfpList}/:role/${ERfpStatus.Draft}`]: [{ roles: [EUserRole.SuperAdmin] }],
  [`${ERoute.RfpList}/:role/:tab`]: [
    { roles: [EUserRole.SuperAdmin] },
    { roles: [EUserRole.AccountAdmin, EUserRole.SellerManager], isSeller: true },
  ],
  // --- /rfp/:status/:id ---
  [`${ERoute.RfpDraft}`]: [{ roles: [EUserRole.SuperAdmin] }],
  [`${ERoute.Rfp}`]: [
    { roles: [EUserRole.SuperAdmin] },
    { roles: [EUserRole.AccountAdmin, EUserRole.SellerManager], isSeller: true },
  ],
};

export const canAccessRoute = (pathname: string, auth: IToken | null, allowedRoutes: AllowedRoutes): boolean => {
  if (!auth) {
    return false;
  }

  const roles = auth.access.roles;
  const isSeller = auth.access.isSeller;

  for (const pattern of Object.keys(allowedRoutes)) {
    const match = matchPath({ path: pattern, end: true }, pathname);

    if (match) {
      const allowedConfigs = allowedRoutes[pattern];

      return allowedConfigs.some((config) => {
        const roleMatch = config.roles.some((role) => roles.includes(role));
        const sellerMatch = config.isSeller === undefined || config.isSeller === isSeller;

        return roleMatch && sellerMatch;
      });
    }
  }

  return false;
};
